import { useEffect, useRef, useState } from 'react';
import { Box, Button, useTheme, useMediaQuery } from '@mui/material';
import RefreshIcon from '@mui/icons-material/RefreshOutlined';
import dayjs from 'dayjs';
import { tokens } from '../../theme';
import { api, apiPrescriptions, IPrescription, IPrescriptionSearchRequest } from '../../types';
import { createApiHeaders } from '../../utilities/JwtTokenParser';
import Header from "../../components/Header";
import Loader from '../../components/Loader';
import DashboardBarChart from './DashboardBarChart';
import DashboardLoader from './DashboardLoader';
import DashboardPieChart from './DashboardPieChart';
import DashboardRecentDoctors from './DashboardRecentDoctors';
import DashboardRecentPatients from './DashboardRecentPatients';

interface MainDashboardProps {
  currentPharmacy: null | string;
}

const fetchDashboardData = async (request: IPrescriptionSearchRequest): Promise<IPrescription[]> => {
  const response = await fetch(`${api}${apiPrescriptions}search`, {
    method: 'POST',
    headers: createApiHeaders(true, true),
    body: JSON.stringify({
      startDate: request.startDate ? dayjs(request.startDate).format('YYYY-MM-DD') : request.startDate,
      endDate: request.endDate ? dayjs(request.endDate).format('YYYY-MM-DD') : request.endDate,
    })
  });
  
  if (response.ok) {
    const json = await response.json();
    return json;
  }

  throw new Error(`Detailed Error Code: ${response.status}.`);
}

const MainDashboard = ({ currentPharmacy }: MainDashboardProps) => {
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // Default: Load the dashboard data for the last "Two Weeks".
  const twoWeeksAgo = new Date();
  twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
  const startDate = useRef(twoWeeksAgo);
  const endDate = useRef(new Date());
  const changeStartDate = (value: Date) => { startDate.current = value };
  const changeEndDate = (value: Date) => { endDate.current = value };

  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [prescriptions, setPrescriptions] = useState<IPrescription[]>([]);

  const refresh = () => {
    setPrescriptions([]);
    setIsLoading(true);
    fetchDashboardData({ 
      startDate: startDate.current, 
      endDate: endDate.current 
    })
    .then((response) => { 
      setPrescriptions(response as IPrescription[]); 
      setIsLoading(false);
      setIsSuccess(true);
    })
    .catch((reason) => {
      console.error(reason);
      setIsLoading(false);
      setIsSuccess(false);
    }); 
  };

  useEffect(() => {
    if (currentPharmacy) {
      refresh();
    }
  }, [currentPharmacy]);

  return (
    <Box m="14px">
      <Box 
        display="grid"
        gridTemplateColumns="repeat(12, 1fr)"
        gap="20px" 
        sx={{
          "& > div": { gridColumn: isNonMobile ? undefined : "span 12" }
        }}
      >
        <Box 
          display="flex"
          sx={{
            gridColumn: "span 4"
          }}
        >
          <Header title="DASHBOARD" subtitle="PHARMACY ACTIVITIES AND STATISTICS" />
        </Box>

        <Box
          display="flex" 
          justifyContent="center"
          sx={{
            gridColumn: "span 4"
          }}
        >
          <Loader isLoading={isLoading} isSuccess={isSuccess} />
        </Box>

        <Box
          display="flex" 
          justifyContent="end"
          sx={{
            gridColumn: "span 4"
          }}
        >
          <Button
            sx={{
              backgroundColor: colors.blueAccent[700],
              color: colors.grey[100],
              fontSize: "14px",
              fontWeight: "bold",
              padding: "8px 14px",
              margin: "30px 0px"
            }}
            onClick={refresh}
          >
            <RefreshIcon sx={{ mr: "8px" }} />
            Refresh
          </Button>
        </Box>

        <Box
          display="flex" 
          justifyContent="center"
          sx={{
            gridColumn: "span 12",
            mb: "14px", 
            mt: "-14px"
          }}
        >
          <DashboardLoader publishStartDate={changeStartDate} publishEndDate={changeEndDate} />
        </Box>
      </Box>

      <Box 
        display="grid"
        gridTemplateColumns="repeat(12, 1fr)"
        gridAutoRows="140px"
        gap="20px" 
        sx={{
          marginTop: "18px"
        }}
      >
        {/* ROW 1 */}
        <Box 
          sx={{
            gridColumn: 'span 6', 
            gridRow: 'span 2',
            backdropFilter: colors.primary[400] 
          }}
        >
          <Box m="-20px 0 0 0">
            <DashboardBarChart prescriptions={prescriptions} calculation='countPerDay' startDate={startDate.current} endDate={endDate.current} />
          </Box>
        </Box>

        <Box 
          sx={{
            gridColumn: 'span 3', 
            gridRow: 'span 2',
            backdropFilter: colors.primary[400] 
          }}
        >
          <Box m="-20px 0 0 0">
            <DashboardRecentPatients prescriptions={prescriptions} />
          </Box>
        </Box>

        <Box 
          sx={{
            gridColumn: 'span 3', 
            gridRow: 'span 2',
            backdropFilter: colors.primary[400] 
          }}
        >
          <Box m="-20px 0 0 0">
            <DashboardRecentDoctors prescriptions={prescriptions} />
          </Box>
        </Box>

        {/* ROW 2 */}
        <Box 
          sx={{
            gridColumn: 'span 6', 
            gridRow: 'span 2',
            backdropFilter: colors.primary[400],
            mt: '70px'
          }}
        >
          <Box m="0 0 0 0">
            <DashboardBarChart prescriptions={prescriptions} calculation='amountPerDay' startDate={startDate.current} endDate={endDate.current} />
          </Box>
        </Box>

        <Box 
          sx={{
            gridColumn: 'span 6', 
            gridRow: 'span 2',
            backdropFilter: colors.primary[400],
            mt: '70px'
          }}
        >
          <Box m="0 0 0 0">
            <DashboardPieChart prescriptions={prescriptions} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default MainDashboard;